/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Suppliers from "./pages/Suppliers";
import Tenders from "./pages/Tenders";
import ItemsFamilies from "./pages/ItemsFamilies";
import PosItems from "./pages/PosItems";
import Orders from "./pages/Orders";
import Marketplace from "./pages/Marketplace";
import SupplierInvoices from "./pages/SupplierInvoices";
import ServiceOrdersWorkspace from "./pages/ServiceOrdersWorkspace";
import StockManagement from "./pages/StockManagement";
import Manufacturing from "./pages/Manufacturing";
import "./PurchasingApp.css";
import RecipesAndCosting from "./pages/RecipesAndCosting";
import MenuAndMargins from "./pages/MenuAndMargins";
import Settings from "./pages/Settings";
import Reports from "./pages/Reports";
import EquivalentProductComparison from "./pages/EquivalentProductComparison";
import ItemLastPurchaseReport from "./pages/ItemLastPurchaseReport";
import PurchasingPanel from "./PurchasingPanel";
import SharedSettings from "../Pages/Settings";
import { readStoredUser } from "../utils/sessionUser";
import PrivateRoute from "../Components/PrivateRoute";
import OriginalDocumentPage from "./components/originals/OriginalDocumentPage";
import UnifiedApprovalsInbox from "./pages/UnifiedApprovalsInbox";
import OcrBackgroundUploadsProvider from "./components/OcrBackgroundUploads";

function LegacyApprovalRedirect({ source }) {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  query.set("source", source);
  return <Navigate replace to={{ pathname: "/purchasing/approvals", search: `?${query}`, hash: location.hash }} />;
}

export default function PurchasingApp() {
  const location = useLocation();
  const sessionUser = readStoredUser();
  const allowedModules = Array.isArray(sessionUser?.allowed_modules) ? sessionUser.allowed_modules : [];

  if (!allowedModules.includes("purchasing")) {
    return <Navigate to="/select-module" replace />;
  }

  return (
    <OcrBackgroundUploadsProvider>
    <Routes>
      <Route path="originals/:id" element={<PrivateRoute><OriginalDocumentPage /></PrivateRoute>} />
      <Route element={<PurchasingPanel />}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="approvals" element={<UnifiedApprovalsInbox />} />
        <Route path="catalog-approvals" element={<LegacyApprovalRedirect source="catalog" />} />

        <Route path="orders/marketplace" element={<Marketplace />} />
        <Route path="orders" element={<Orders />} />
        <Route path="orders/requisitions" element={<Orders forcedTab="requisitions" />} />
        <Route path="orders/purchase-orders" element={<Orders forcedTab="orders" />} />
        <Route path="orders/approvals" element={<LegacyApprovalRedirect source="purchase_orders" />} />
        <Route path="orders/receivings" element={<Orders forcedTab="receivings" />} />
        <Route path="orders/supplier-returns" element={<Orders forcedTab="supplierReturns" />} />
        <Route path="orders/delivery-notes" element={<Orders forcedTab="deliveryNotes" />} />
        <Route path="supplier-invoices" element={<SupplierInvoices />} />

        <Route path="service-orders" element={<Navigate replace to={{
          pathname: "/purchasing/service-orders/requisitions", search: location.search, hash: location.hash,
        }} />} />
        <Route path="service-orders/requisitions" element={<ServiceOrdersWorkspace mode="requisitions" />} />
        <Route path="service-orders/orders" element={<ServiceOrdersWorkspace mode="orders" />} />
        <Route path="service-orders/confirmations" element={<ServiceOrdersWorkspace mode="confirmations" />} />
        <Route path="service-orders/approvals" element={<LegacyApprovalRedirect source="service_orders" />} />
        <Route path="service-orders/settings" element={<Navigate replace to={{
          pathname: "/purchasing/settings/services", search: location.search, hash: location.hash,
        }} />} />

        <Route path="stock-management" element={<StockManagement />} />
        <Route path="stock-management/manufacturing" element={<Manufacturing />} />
        <Route
          path="stock-management/inventory-intelligence"
          element={<StockManagement forcedTab="currentStock" />}
        />
        <Route
          path="stock-management/current-stock"
          element={<StockManagement forcedTab="currentStock" />}
        />
        <Route
          path="stock-management/requisitions"
          element={<StockManagement forcedTab="stockRequisitions" />}
        />
        <Route
          path="stock-management/requisitions/approvals"
          element={<LegacyApprovalRedirect source="stock_requisitions" />}
        />
        <Route path="stock-management/returns" element={<StockManagement forcedTab="stockReturns" />} />
        <Route
          path="stock-management/transfers/approvals"
          element={<LegacyApprovalRedirect source="stock_transfers" />}
        />
        <Route
          path="stock-management/transfers"
          element={<StockManagement forcedTab="stockTransfers" />}
        />
        <Route
          path="stock-management/dispatched"
          element={<StockManagement forcedTab="stockMovements" />}
        />
        <Route
          path="stock-management/inventory-count"
          element={<StockManagement forcedTab="inventoryCount" />}
        />
        <Route
          path="stock-management/snapshot-report"
          element={<StockManagement forcedTab="snapshotReport" />}
        />

        <Route path="items" element={<ItemsFamilies />} />
        <Route path="items/tenders" element={<Tenders />} />
        <Route path="items/items" element={<ItemsFamilies forcedTab="items" />} />
        <Route path="items/equivalent-products" element={<ItemsFamilies forcedTab="equivalent-products" />} />
        <Route path="items/pos-catalog" element={<PosItems />} />
        <Route path="items/subcategories" element={<ItemsFamilies forcedTab="subcategories" />} />
        <Route path="items/categories" element={<ItemsFamilies forcedTab="categories" />} />
        <Route path="items/subfamilies" element={<ItemsFamilies forcedTab="subfamilies" />} />
        <Route path="items/families" element={<ItemsFamilies forcedTab="families" />} />

        <Route path="recipes" element={<RecipesAndCosting />} />
        <Route path="recipes/menu" element={<MenuAndMargins />} />

        <Route path="reports" element={<Reports forcedReport="itemsPurchased" />} />
        <Route path="reports/items-purchased" element={<Reports forcedReport="itemsPurchased" />} />
        <Route path="reports/equivalent-products" element={<EquivalentProductComparison />} />
        <Route path="reports/item-last-purchase" element={<ItemLastPurchaseReport />} />

        <Route path="settings" element={<Settings />} />
        <Route path="settings/units" element={<Settings forcedTab="units" />} />
        <Route path="settings/company" element={<Settings forcedTab="company" />} />
        <Route path="settings/access" element={<Settings forcedTab="access" />} />
        <Route
          path="settings/users"
          element={<SharedSettings forcedTab="users" allowPurchasingSharedAccess />}
        />
        <Route
          path="settings/appearance"
          element={<SharedSettings forcedTab="appearance" allowPurchasingSharedAccess />}
        />
        <Route path="settings/orders" element={<Settings forcedTab="orders" />} />
        <Route path="settings/services" element={<Settings forcedTab="services" />} />
        <Route path="settings/locations" element={<Settings forcedTab="locations" />} />
        <Route path="settings/whatsapp" element={<Settings forcedTab="whatsapp" />} />

        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Route>
    </Routes>
    </OcrBackgroundUploadsProvider>
  );
}
